import React, { useState, useMemo, useEffect } from 'react'
import _, { isEmpty } from 'lodash'
import classNames from 'classnames'
import GridListFilter from './components/GridListFilter'
import Pagination from '../Pagination'
import ListSelectedItems from 'components/ListSelectedItems'
import GridListLoading from './components/GridListLoading'
import GridListTable from './components/GridListTable'
import { getOutput } from 'components/Filter/clientFilter'
import { paginateData, manageColumnsData } from './utils'
import { caseInsensitiveSort } from '../../utils/common'
import './style.scss'
import { getColumnState, saveColumnState, getSortState, saveSortState, getCardViewState, saveCardViewState } from 'utils/tableStorage';

const GridList = ({ gridDefs, gridData, gridProps = {}, tableId }) => {
  const savedSort = tableId ? getSortState(tableId) : null
  const savedCardView = tableId ? getCardViewState(tableId) : null
  const { rowProps = {}, color, sortProps = {}, paginationProps = {}, selectedRows, filterProps, isLoading, isBordered, isScrollable, noSelectDisableIds, gridView } = gridProps

  const { rowSelect, rowId = '_id', rowSingleSelect, disabledClickIds, setAsSelectedIds = [] } = rowProps
  const { sortBy, enableSort } = sortProps
  const { enablePagination, onPageChange, onLimitChange, currentPage, onPaginationAction, totalCount, enabledServerSidePagination = false, pageLimit, dropUp, alwaysShowPagination = false } = paginationProps
  const [sortedBy, setSortedBy] = useState(savedSort?.key || sortBy?.sortColumn || 'name')
  const [sortedAsc, setSortedAsc] = useState(
    savedSort?.direction ? savedSort.direction === 'asc' : (sortBy?.sortAsc ?? true)
  )
  const [paginationCurrent, setPaginationCurrent] = useState(currentPage || 1)
  const [paginationLimit, setPaginationLimit] = useState(pageLimit || 10)
  const [renderedColumns, setRenderedColumns] = useState(manageColumnsData(gridDefs) || [])
  const [selectedIds, setSelectedIds] = useState(selectedRows || [])
  const [resetFilter, setResetFilter] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(null)
  const [gridList, setGridList] = useState([])
  const [searchName, setSearchName] = useState('')
  const [isSelectedAll, setIsSelectedAll] = useState((gridList.length - (!isEmpty(noSelectDisableIds) ? disabledClickIds.length : 0)) === selectedIds.length)

  const selectedCount = (() => {
    const s = new Set(selectedIds)
    if (!isEmpty(setAsSelectedIds)) {
      setAsSelectedIds.forEach(id => s.add(id))
    }
    return s.size
  })()

  const onSortAsc = (data) => {
    setSortedAsc(data)
    tableId && saveSortState(tableId, {key: sortedBy, direction: data ? 'asc' : 'desc'});
  } 

  const onSortBy = (data) => {
    setSortedBy(data)
    tableId && saveSortState(tableId, {key: data, direction: sortedAsc ? 'asc' : 'desc'});
  }

  // Save gridView to localStorage when it changes
  useEffect(() => {
    if (tableId && gridView) {
      saveCardViewState(tableId, gridView);
    }
  }, [gridView, tableId])

  useEffect(() => {
    setGridList(gridData)
  }, [gridData])

  useEffect(() => {
    setSelectedIds(selectedRows || [])
  }, [selectedRows])

  useEffect(() => {
    let processedColumns = manageColumnsData(gridDefs) || [];
    
    // Apply saved column state if tableId is provided
    if (tableId) {
      const savedColumnState = getColumnState(tableId);
      if (savedColumnState && Array.isArray(savedColumnState)) {
        processedColumns = processedColumns.map((column) => {
          const savedColumn = savedColumnState.find(
            (saved) => saved.sortKey === column.sortKey
          );
          if (savedColumn) {
            return { ...column, isVisible: savedColumn.isVisible };
          }
          return column;
        });
      }
    }

    setRenderedColumns(processedColumns)
  }, [gridDefs, tableId])

  const sortData = useMemo(() => {
    return _.orderBy(gridList, [(item) => caseInsensitiveSort(item[sortedBy])], [sortedAsc ? 'asc' : 'desc'])
    },
    [gridList, sortedBy, sortedAsc]
  )

  const paginatedData = useMemo(() => {
    return paginateData(sortData, paginationCurrent, paginationLimit)
    },
    [sortData, paginationCurrent, paginationLimit]
  )

  const displayData = () => {
    if (enabledServerSidePagination) {
      return gridList
    }
    if (enablePagination) {
      return paginatedData
    } else if (enableSort) {
      return sortData
    } else {
      return gridList
    }
  }

  const onRowSelect = (item) => {
    let updateSelected = []

    if (selectedIds.includes(item[rowId])) {
      if (rowSingleSelect) {
        updateSelected = []
      } else {
        updateSelected = selectedIds.filter(id => id !== item[rowId])
      }
    } else {
      if (rowSingleSelect) {
        updateSelected = [item[rowId]]
      } else {
        updateSelected = [...selectedIds, item[rowId]]
      }
    }

    rowSelect && rowSelect(updateSelected)
    setSelectedIds(updateSelected)
  }

  const onSelectAll = () => {
    let updateSelected = []
    let getSelectableIds = gridList.map(item => item[rowId])

    if (!isEmpty(disabledClickIds)) {
      getSelectableIds = getSelectableIds.filter(id => !disabledClickIds.includes(id))
    }

    if (selectedIds.length !== getSelectableIds.length) {
      updateSelected = getSelectableIds
    }

    rowSelect && rowSelect(updateSelected)
    setSelectedIds(updateSelected)
    setIsSelectedAll(checkIfAllSelected())
  }

  const checkIfAllSelected = () => {
    let getSelectableIds = gridList.map(item => item[rowId])

    if (disabledClickIds) {
      getSelectableIds = getSelectableIds.filter(id => !disabledClickIds.includes(id))
    }

    return selectedIds.length === getSelectableIds.length
  }

  const updateSearch = (name) => {
    setPaginationCurrent(1)
    const filteredData = selectedFilters ? getOutput(selectedFilters?.filterQuery, gridData) : gridData

    let result = filteredData

    if (name) {
      result = filteredData.filter((item) => item.name.toLowerCase().includes(name.toLowerCase()))
    }
    setSearchName(name)
    setGridList(result)
  }

  const onReset = () => {
    rowSelect && rowSelect([])
    setSelectedIds([])
    setResetFilter(!resetFilter)
    setSelectedFilters(null)
    setPaginationCurrent(1)
    setGridList(gridData)
    setSearchName('')
    filterProps?.onFilter && filterProps.onFilter()
  }

  return (
    <div className={classNames('viso-grid-list', isScrollable && 'viso-grid-list--scrollable')}>
      {filterProps &&
        <div className='viso-grid-list__filters'>
          <div className={classNames(isLoading && 'hide')}>
            {!filterProps?.filterHide &&
              <GridListFilter
                {...filterProps}
                data={gridData}
                color={color}
                onFilterAction={filterProps?.onFilter}
                onSearchAction={filterProps?.onSearch}
                resetFilter={resetFilter}
                updateFilters={setSelectedFilters}
                updateGridList={setGridList}
                updateSearch={updateSearch}
                searchName={searchName}
                changePage={setPaginationCurrent}
              />
            }
          </div>
          <div className='viso-grid-list__selection'>
            <ListSelectedItems
              // selectedNumber={selectedIds.length}
              selectedNumber={selectedCount}
              onReset={onReset}
              filterData={selectedFilters?.filterQuery}
              containerClassName={color}
              search={searchName}
              hideResetButton={filterProps?.hideResetButton}
              hideClearSelectedButton={filterProps?.hideClearSelectedButton}
            />
          </div>
        </div>
      }
      {isLoading ?
        <GridListLoading />
        :
        <GridListTable
          tableId={tableId}
          sortedBy={sortedBy}
          sortedAsc={sortedAsc}
          setSortedBy={onSortBy}
          setSortedAsc={onSortAsc}
          selectedIds={selectedIds}
          onRowSelect={onRowSelect}
          renderedColumns={renderedColumns}
          isBordered={isBordered}
          filterProps={filterProps}
          gridProps={gridProps}
          onSelectAll={onSelectAll}
          isSelectedAll={checkIfAllSelected()}
          sortProps={sortProps}
          setPaginationCurrent={setPaginationCurrent}
          setRenderedColumns={(data) => {
            if (tableId) {
              saveColumnState(tableId, data);
            }
            setRenderedColumns(data)
          }}
          displayData={displayData}
        />
      }
      {enablePagination && (alwaysShowPagination || (enabledServerSidePagination ? ((totalCount || 0) > paginationLimit) : (gridData.length > paginationLimit))) &&
        <div className={classNames('viso-grid-list-pagination', isBordered && 'viso-grid-list-pagination--bordered', isLoading && 'hide')}>
          <Pagination
            tableId={tableId}
            dropUp={dropUp}
            hideDivider={isBordered}
            currentPage={paginationCurrent}
            limit={paginationLimit}
            onChangeLimit={(limit) => {
              onLimitChange && onLimitChange(limit)
              setPaginationLimit(limit)

              if (onPaginationAction) {
                onPaginationAction({ limit, offset: paginationCurrent <= 1 ? 0 : (paginationCurrent - 1) * limit })
              }
            }}
            totalPage={totalCount || gridList.length}
            setCurrentPage={(page) => {
              onPageChange && onPageChange(page)
              setPaginationCurrent(page)

              if (onPaginationAction) {
                onPaginationAction({ limit: paginationLimit, offset: page <= 1 ? 0 : (page - 1) * paginationLimit })
              }
            }}
          />
        </div>
      }
      {isBordered && <div className='viso-grid-list-border' />}
    </div>
  )
}

export default GridList